import React from 'react';
import { RiAlertLine, RiArrowRightSLine, RiInformationLine } from '@remixicon/react';
import { Ticket as TicketType, TicketStatus, TicketType as TicketTypeEnum } from '@/lib/OrderApi';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../shared/Accordion';
import * as m from '@/paraglide/messages.js';

/**
 * FAS-974 — one line under each ticket saying what still has to happen with it, with
 * "more info" opening the routes the holder can take.
 *
 * Before this, a ticket that needed claiming looked exactly like one that was finished:
 * the only hint was a small icon caption. The deadline in particular existed server-side
 * for a long time and was never shown to anyone.
 */

export type TicketRoute = 'personalise' | 'share' | 'sell' | 'return' | 'amend';

interface Props {
    ticket: TicketType;
    eventName: string;
    marketplaceUrl: string | null;
    onChoose: (route: TicketRoute) => void;
}

/**
 * What does this ticket still need? Returns null when there is nothing to do, which is
 * the common case for a finished ticket and keeps the row quiet.
 */
function actionFor(ticket: TicketType): string | null {
    if (ticket.requiresPersonalisationUpdate) {
        return m.ticket_action_details();
    }

    if (ticket.ticketStatus === TicketStatus.claimable) {
        // FAS-974 follow-up — you hold at most one ticket in your own name. Once one is
        // claimed the API marks the rest not-self-claimable, and telling their owner they
        // still have to "claim" them is a dead end: the only route left is sharing.
        // Matches the banner at the top of the page, which already said exactly this.
        return isSelfClaimable(ticket) ? m.ticket_action_claim() : m.ticket_action_share();
    }

    if (ticket.ticketStatus === TicketStatus.shared) {
        const name = 'shareName' in ticket ? ticket.shareName : undefined;
        return name ? m.ticket_action_shared_with({ name }) : m.ticket_action_shared();
    }

    // FAS-1070 — the mirror of "you received this from X": once someone has claimed a
    // ticket you shared, your own order page has to say who now holds it. The name was
    // in the API all along (`claimedBy`, the claimer's first name); the only place it
    // surfaced was an untranslated caption in the 100px tile, which is not where anyone
    // looks for it.
    if (ticket.ticketStatus === TicketStatus.claimed) {
        const name = 'claimedBy' in ticket ? ticket.claimedBy : undefined;
        return name ? m.ticket_claimed_by({ name }) : m.ticket_claimed();
    }

    return null;
}

/**
 * Does this ticket still offer "put it in my own name"? The API sends `isSelfClaimable`
 * on claimable tickets only, and it is false as soon as another ticket in the order is
 * held by the buyer. Absent (a non-claimable ticket) reads as "no".
 */
function isSelfClaimable(ticket: TicketType): boolean {
    return 'isSelfClaimable' in ticket && ticket.isSelfClaimable === true;
}

const TicketActionLine: React.FC<Props> = ({ ticket, eventName, marketplaceUrl, onChoose }) => {
    // FAS-1070 — read the status up front. The guards below narrow the union to the
    // members that declare `ticketType`, which the ClaimedTicket type does not (the API
    // does send it), so the same comparison further down is a compile error.
    const isClaimedBySomeoneElse = ticket.ticketStatus === TicketStatus.claimed;

    // Group and donation rows are not personalised, so they never carry an action.
    const isActionableType =
        'ticketType' in ticket
        && ticket.ticketType !== TicketTypeEnum.GroupedTicket
        && ticket.ticketType !== TicketTypeEnum.DonationTicket;

    if (!isActionableType) {
        return null;
    }

    // Nothing left to do or say once the ticket has left the holder's hands. Without
    // this a just-returned ticket kept offering "give it back".
    const isFinished = ([
        TicketStatus.revoked,
        TicketStatus.refunded,
        TicketStatus.sold,
        TicketStatus.blocked,
    ] as TicketStatus[]).includes(ticket.ticketStatus);

    if (isFinished) {
        return null;
    }

    const received = ticket.receivedThroughShare === true;
    const needsDetails = ticket.requiresPersonalisationUpdate === true;
    const sharedBy = ticket.sharedByName ?? null;
    const action = received && !needsDetails
        ? (sharedBy !== null ? m.ticket_received_from({ name: sharedBy }) : m.ticket_received_intro())
        : actionFor(ticket);

    if (action === null) {
        return null;
    }

    // A ticket you received cannot be shared on and cannot be claimed by you again — the
    // only route is handing it back, so the menu collapses to that one option.
    //
    // FAS-974 follow-up — "this is my own ticket" also disappears once another ticket in
    // this order is already in your name. The API has always computed that
    // (isSelfClaimable, which the ticket tile on the left renders correctly); this menu
    // was the one place that ignored it, so it kept offering a route the server now
    // refuses outright.
    const canPersonalise = !received
        && ticket.ticketStatus === TicketStatus.claimable
        && isSelfClaimable(ticket);
    const canShare = !received && ticket.canShare !== false && ticket.ticketStatus === TicketStatus.claimable;
    // Only offer the marketplace when this event actually has one — otherwise it is a
    // dead-end button (the prep call: two routes are better than three that don't work).
    // Reselling a received ticket IS allowed: the one-hop rule is about sharing only.
    const canSell = marketplaceUrl !== null
        && 'canResell' in ticket
        && ticket.canResell === true;

    // A shared ticket is waiting on someone else: there is nothing for this reader to
    // choose, so the line states the situation and stops rather than opening onto an
    // empty menu.
    // The API decides: only a ticket that was given away for free can be handed back,
    // and only while it is still available (not listed, sold or scanned).
    const canReturn = ticket.canReturn === true;
    const hasRoutes = needsDetails || canReturn || canPersonalise || canShare || canSell;

    // A received ticket is a statement of fact, not a to-do: the holder can keep it and
    // do nothing. Amber "Action required" there would cry wolf, so it reads as info.
    // A ticket somebody else has already claimed is the same kind of statement: it has
    // left this holder's hands and there is nothing left for them to do with it.
    const isInformational = (received || isClaimedBySomeoneElse) && !needsDetails;
    const Icon = isInformational ? RiInformationLine : RiAlertLine;

    return (
        <div
            className={
                'mt-1 rounded-md px-3 py-2 text-sm border '
                + (isInformational
                    ? 'bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] border-[var(--shop-borderColor)]'
                    : 'bg-amber-100 text-amber-900 border-amber-200')
            }
        >
            <div className="flex items-start gap-2">
                <Icon size={16} className="mt-[2px] flex-shrink-0" />
                <p className="tracking-tight">
                    {!isInformational && (
                        <>
                            <span className="font-semibold">{m.ticket_action_required()}</span>
                            {' — '}
                        </>
                    )}
                    {action}
                </p>
            </div>

            {hasRoutes && (
            <Accordion type="single" collapsible>
                <AccordionItem value="more-info" className="border-none">
                    {/* The shared trigger ships a 40px chevron sized for section headers;
                        scale it down and pull the pair together so this reads as a link. */}
                    <AccordionTrigger
                        canCollapse
                        className="py-1 pl-6 text-sm font-semibold justify-start gap-0.5 [&>svg]:h-4 [&>svg]:w-4"
                    >
                        {m.ticket_action_more_info()}
                    </AccordionTrigger>
                    <AccordionContent className="pl-6 pb-1">
                        {needsDetails ? (
                            <RouteButton
                                title={m.ticket_amend_personalisation_cta()}
                                description={m.ticket_amend_personalisation_intro()}
                                onClick={() => onChoose('amend')}
                            />
                        ) : received ? (
                            <div className="space-y-2">
                                {/* Only true for a free hand-over — a paid one stays shareable. */}
                                {ticket.canShare === false && (
                                    <p className="tracking-tight">{m.ticket_received_explainer()}</p>
                                )}
                                {canReturn && (
                                    <RouteButton
                                        title={m.ticket_return_button()}
                                        onClick={() => onChoose('return')}
                                    />
                                )}
                                {canSell && (
                                    <RouteButton
                                        title={m.ticket_route_sell({ eventName })}
                                        description={m.ticket_route_sell_desc()}
                                        onClick={() => onChoose('sell')}
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <p className="font-semibold tracking-tight">{m.ticket_routes_title()}</p>
                                {canPersonalise && (
                                    <RouteButton
                                        title={m.ticket_route_own()}
                                        description={m.ticket_route_own_desc()}
                                        onClick={() => onChoose('personalise')}
                                    />
                                )}
                                {canShare && (
                                    <RouteButton
                                        title={m.ticket_route_share()}
                                        description={m.ticket_route_share_desc()}
                                        onClick={() => onChoose('share')}
                                    />
                                )}
                                {canSell && (
                                    <RouteButton
                                        title={m.ticket_route_sell({ eventName })}
                                        description={m.ticket_route_sell_desc()}
                                        onClick={() => onChoose('sell')}
                                    />
                                )}
                            </div>
                        )}
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
            )}
        </div>
    );
};

/**
 * Tinted with the container's own text color rather than a hardcoded white fill: the
 * informational variant sits on --shop-panelBackgroundColor and inherits
 * --shop-textColor, so `bg-white` rendered white-on-white for dark shop themes.
 */
const RouteButton: React.FC<{ title: string; description?: string; onClick: () => void }> = ({
    title,
    description,
    onClick,
}) => (
    <button
        type="button"
        onClick={onClick}
        className="w-full text-left flex items-center justify-between gap-2 rounded-md border border-current/20 bg-current/5 hover:bg-current/10 px-3 py-2 transition-colors"
    >
        <span>
            <span className="block font-semibold tracking-tight">{title}</span>
            {description !== undefined && (
                <span className="block text-xs tracking-tight opacity-80">{description}</span>
            )}
        </span>
        <RiArrowRightSLine size={18} className="flex-shrink-0" />
    </button>
);

export default TicketActionLine;
